import React from "react";
import { Stack, Typography, styled, Box } from "@mui/material";
import { DOWNLOAD_BTN_VARIANTS } from "@consts";
import DownloadButton from "@hyro-components/DownloadButton";
import Tooltip from "@hyro-components/Tooltip";
import Chip from "@hyro-components/Chip";
import SearchBar from "@hyro-components/SearchBar";
import { HyroFilter } from "@hyro-components";
import DefaultFilters from "@hyro-components/hyro-filter/components/default-filters/DefaultFilters";
import useFilter from "@hyro-components/hyro-filter/components/filter-panel/FilterPanel.hook";
import RefreshButton from "@hyro-components/hyro-filter/components/refresh-button/RefreshButton";
import QuestionIcon from "src/icons/insights/QuestionIcon";

const EMPTY_ARRAY = [];

const HyroHeader = ({
  testId,
  title,
  titleVariant = "h6",
  titleAdornment,
  subtitle,
  rowsLength,
  isReadOnly,
  showMainSection = true,
  showDownloadButton = true,
  showSearchBar = true,
  enableFilter = true,
  downloadFileName,
  downloadFileData = EMPTY_ARRAY,
  downloadFileHeaders = EMPTY_ARRAY,
  handleSearch,
  filters = EMPTY_ARRAY,
  disableSearch = false,
  searchQueryParam,
  filterQueryParam,
  searchText,
  disableFilters = false,
  isBulkEditActive = false,
  setFilters,
  children,
  infoTooltipText,
  errorText,
  refreshButtonProps = {
    showRefreshButton: false,
    autoRefreshInterval: 30000,
    handleRefresh: () => { },
    isRefreshing: false
  },
}) => {
  const {
    activeFilters,
    inactiveFilters,
    handleChangeFilterField,
    handleRemoveFilter,
    handleClearAll,
    handleAddFilter
  } = useFilter({ filters, setFilters, filterQueryParam });

  const defaultFilters = filters.filter((filter) => filter.isDefault);
  const regularActiveFilters = activeFilters.filter((filter) => !filter.isDefault);
  const regularInactiveFilters = inactiveFilters.filter((filter) => !filter.isDefault);

  const showFilterButton = enableFilter;
  const showFilterPanel = showFilterButton && regularActiveFilters.length > 0;

  return <>
    {showMainSection && <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5} mb={showFilterPanel || subtitle ? 0 : 2} data-testid={`table-header-${testId}`}>
      <Stack direction="row" alignItems="center" gap={3}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          {title && <>
            <Typography variant={titleVariant} component="span" data-testid={`table-header-${testId}-title`}>{title}</Typography>

            <Typography variant="body2" component="span" data-testid={`table-header-${testId}-rows-length`} sx={{ mt: "3px" }}>{`(${rowsLength || 0})`}</Typography>
          </>}
          {infoTooltipText && <Tooltip
            title={infoTooltipText}
            placement="bottom-start"
            tooltipStyle={{ maxWidth: "350px" }}
            variant="light"
          >
            <Box sx={{ width: 20, height: 20 }}>
              <QuestionIcon size={20} />
            </Box>
          </Tooltip>}
          {isReadOnly &&
          <Tooltip
            popperStyle={{ marginTop: "6px", width: "156px" }}
            placement="bottom"
            title="This table is informational only and cannot be edited."
          >
            <Chip
              data-testid={`table-header-${testId}-read-only-chip`}
              label="Read Only"
              backgroundColorPath={["grey", "200"]}
              textColorPath={["grey", "700"]}
            />
          </Tooltip>
          }
          {errorText && <Typography variant="caption" component="span" data-testid={`table-header-${testId}-error`} sx={{ color: "error.main" }}>
            {errorText}
          </Typography>}
          {titleAdornment}
        </Stack>
        <DefaultFilters filters={defaultFilters} disabled={disableFilters} onChange={handleChangeFilterField} />
      </Stack>

      <Stack direction="row" alignItems="center" gap={1}>
        {refreshButtonProps?.showRefreshButton && <RefreshButton
          onClick={refreshButtonProps?.handleRefresh}
          isRefreshing={refreshButtonProps?.isRefreshing}
          autoRefreshInterval={refreshButtonProps?.autoRefreshInterval}
          disabled={refreshButtonProps?.disabled}
        />}

        {showSearchBar && <SearchBar testId={`table-header-${testId}`} initialValue={searchText} onChange={handleSearch} disabled={disableSearch} searchQueryParam={searchQueryParam} />}

        {showFilterButton &&
          <HyroFilter
            button
            testId={`table-header-${testId}`}
            filterOptions={regularInactiveFilters}
            disabled={disableFilters}
            isBulkEditActive={isBulkEditActive}
            onFilterOptionClick={handleAddFilter}
          />
        }

        {showDownloadButton &&
          <DownloadButton
            testId={`table-header-${testId}-download-btn`}
            variant={DOWNLOAD_BTN_VARIANTS.OUTLINED}
            fileName={downloadFileName}
            fileData={downloadFileData}
            fileHeaders={downloadFileHeaders}
          />
        }

        {children}
      </Stack>
    </Stack>}
    {showMainSection && subtitle && <Typography variant="body2" component="span" data-testid={`table-header-${testId}-subtitle`} sx={{ color: "text.secondary", display: "block", mb: 2 }}>
      {subtitle}
    </Typography>}

    {showFilterPanel &&
      <StyledFilterPanelContainer showMainSection={showMainSection}>
        <HyroFilter
          panel
          disabled={disableFilters}
          isBulkEditActive={isBulkEditActive}
          activeFilters={regularActiveFilters}
          inactiveFilters={regularInactiveFilters}
          handleAddFilter={handleAddFilter}
          handleChangeFilterField={handleChangeFilterField}
          handleRemoveFilter={handleRemoveFilter}
          handleClearAll={handleClearAll}
        />
      </StyledFilterPanelContainer>
    }
  </>;
};

const StyledFilterPanelContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "showMainSection"
})(({ theme, showMainSection }) => ({
  paddingTop: theme.spacing(showMainSection ? 1.5 : 0.5)
}));

export default HyroHeader;
